import { createRoot, createSignal, onCleanup } from 'solid-js';
import { pushEvent } from '~/lib/event-log';

export interface Fix {
  timestamp: number;
  lat: number;
  lng: number;
  /** Accuracy in meters, as reported by the platform. */
  accuracy: number;
  /** Speed in m/s if reported (null otherwise). */
  speed: number | null;
  /** Heading in degrees if reported (null otherwise). */
  heading: number | null;
  /** Altitude in meters if reported (null otherwise). */
  altitude: number | null;
  /** True if this fix came from the ghost-walk simulator. */
  simulated: boolean;
  /** True if the helper was in `tracking` state when this fix arrived,
   *  i.e. cold-start accuracy jitter has stabilized. False during the
   *  `acquiring` window. Useful for downstream consumers (anti-cheat,
   *  gameplay gates) that should ignore unsettled fixes. */
  settled: boolean;
}

export type PermissionState = 'unknown' | 'prompt' | 'granted' | 'denied' | 'unsupported';
/** Tracking lifecycle. `acquiring` is the cold-start window where
 *  watchPosition has been started and Wake Lock is held but the helper
 *  has not yet received a fix with accuracy below the settled threshold.
 *  See architecture doc Addendum A for the empirical basis. */
export type TrackingState = 'idle' | 'acquiring' | 'tracking' | 'error';

/** Identifies which screen owns the current tracking session. The MAP
 *  screen auto-starts on mount and releases on unmount, while DIAG keeps
 *  a session alive until the user explicitly stops it. Either screen can
 *  freely start when the other already has tracking running — the second
 *  call is a no-op and the original owner is preserved. */
export type StartedBy = 'map' | 'diag' | null;

/** Result of probing the geolocation permission state without firing
 *  the browser permission prompt. `unsupported` covers Safari versions
 *  whose Permissions API doesn't accept `geolocation`. */
export type GeolocationPermissionState = 'granted' | 'denied' | 'prompt' | 'unsupported';

/** Read-only probe of geolocation permission. Never triggers a prompt;
 *  used by MAP to decide whether to show the explanatory pre-prompt or
 *  skip directly to `start()`. */
export async function checkGeolocationPermission(): Promise<GeolocationPermissionState> {
  if (typeof navigator === 'undefined' || !('geolocation' in navigator)) return 'unsupported';
  const perms = (navigator as Navigator & { permissions?: Permissions }).permissions;
  if (!perms || typeof perms.query !== 'function') return 'unsupported';
  try {
    const status = await perms.query({ name: 'geolocation' as PermissionName });
    const state = status.state as GeolocationPermissionState;
    if (state === 'granted' || state === 'denied' || state === 'prompt') return state;
    return 'unsupported';
  } catch {
    // Older iOS Safari throws for unrecognized PermissionName.
    return 'unsupported';
  }
}

export const WATCH_OPTIONS: PositionOptions = {
  enableHighAccuracy: true,
  maximumAge: 0,
  timeout: 15000,
};

/** Geolocation fix buffer cap. At the empirical ~0.7Hz with gaps observed
 *  in walk 2 of the field test (Addendum A), 5000 fixes covers roughly
 *  2 hours of session time — sufficient for any realistic alpha walk. */
export const FIX_BUFFER_CAP = 5000;

/** Accuracy floor (meters) below which a fix is considered "settled" and
 *  the helper transitions from `acquiring` to `tracking`. 20m is the
 *  worst median outdoor accuracy observed in the Trinidad field tests;
 *  setting the threshold there lets the helper move out of cold-start
 *  quickly under typical conditions while ignoring the >50m first-fix
 *  jitter Safari sometimes emits. */
export const ACCURACY_SETTLED_THRESHOLD_M = 20;

export interface GeolocationTracker {
  state: () => TrackingState;
  permission: () => PermissionState;
  latest: () => Fix | null;
  fixCount: () => number;
  /** All buffered fixes (capped at FIX_BUFFER_CAP, most recent last). */
  fixes: () => Fix[];
  startedAt: () => number | null;
  lastError: () => string | null;
  /** Numeric code of the most recent `GeolocationPositionError`, or null
   *  if no platform error has fired this session. 1 = PERMISSION_DENIED,
   *  2 = POSITION_UNAVAILABLE, 3 = TIMEOUT per the W3C Geolocation API.
   *  Exposed separately from `lastError` so the UI can distinguish a real
   *  re-grant case from a transient signal loss. */
  lastErrorCode: () => number | null;
  /** Which screen owns the current session, or null when idle. Set by
   *  whichever screen first calls `start()`; preserved across no-op
   *  re-entrant `start()` calls; cleared on `stop()`. */
  startedBy: () => StartedBy;
  /** Start tracking. Idempotent: when the helper is already `acquiring`
   *  or `tracking`, this is a no-op and the existing `startedBy` owner
   *  is preserved. */
  start: (by?: Exclude<StartedBy, null>) => Promise<void>;
  /** Stop tracking. Safe to call from any state. */
  stop: () => void;
  /** Inject a synthetic fix without going through `watchPosition`. */
  pushSimulatedFix: (lat: number, lng: number, speed?: number) => void;
  /** While true, the real `watchPosition` callback ignores incoming device
   *  fixes so an active ghost-walk's `pushSimulatedFix` is the sole position
   *  source app-wide. The ghost-walk singleton sets it on start / clears it on
   *  stop. No effect on real walks (it stays false). */
  setSimOverride: (active: boolean) => void;
  clearLog: () => void;
}

export function createGeolocationTracker(): GeolocationTracker {
  const supported = typeof navigator !== 'undefined' && 'geolocation' in navigator;
  const [state, setState] = createSignal<TrackingState>('idle');
  const [permission, setPermission] = createSignal<PermissionState>(
    supported ? 'unknown' : 'unsupported',
  );
  const [latest, setLatest] = createSignal<Fix | null>(null);
  const [fixCount, setFixCount] = createSignal(0);
  const [fixes, setFixes] = createSignal<Fix[]>([]);
  const [startedAt, setStartedAt] = createSignal<number | null>(null);
  const [lastError, setLastError] = createSignal<string | null>(null);
  const [lastErrorCode, setLastErrorCode] = createSignal<number | null>(null);
  const [startedBy, setStartedBy] = createSignal<StartedBy>(null);

  let watchId: number | null = null;
  // When a ghost-walk is active it owns the position feed; the real watcher's
  // fixes are dropped so the simulated position is authoritative everywhere.
  let simOverride = false;

  const appendFix = (fix: Fix) => {
    setLatest(fix);
    setFixCount((c) => c + 1);
    setFixes((list) => {
      const next = [...list, fix];
      if (next.length > FIX_BUFFER_CAP) next.splice(0, next.length - FIX_BUFFER_CAP);
      return next;
    });
  };

  const setPermissionState = (next: PermissionState) => {
    if (permission() === next) return;
    setPermission(next);
    pushEvent('geolocation_permission_changed', { state: next });
  };

  const queryPermission = async () => {
    if (!supported) return;
    const perms = (navigator as Navigator & { permissions?: Permissions }).permissions;
    if (!perms || typeof perms.query !== 'function') return;
    try {
      const status = await perms.query({ name: 'geolocation' as PermissionName });
      setPermissionState(status.state as PermissionState);
      status.addEventListener?.('change', () =>
        setPermissionState(status.state as PermissionState),
      );
    } catch {
      // Permissions API not implemented for geolocation on this platform (Safari pre-16).
    }
  };

  /** Idempotency contract: when the helper is already `acquiring` or
   *  `tracking`, `start()` is a no-op and the original `startedBy` owner
   *  is preserved. The `watchId` check below is a redundant safety net
   *  in case the state signal gets out of step with the platform handle. */
  const start = async (by: Exclude<StartedBy, null> = 'map') => {
    if (!supported) {
      setLastError('Geolocation API not available');
      setState('error');
      return;
    }
    if (state() === 'acquiring' || state() === 'tracking') return;
    if (watchId !== null) return;
    // Clear stale error state from a previous failed session so the UI
    // banner (which keys on tracker state === 'error') unmounts as soon
    // as a retry actually begins.
    setLastErrorCode(null);
    setStartedBy(by);
    setState('acquiring');
    setStartedAt(Date.now());
    pushEvent('tracking_started', { by });
    await queryPermission();
    /* eslint-disable solid/reactivity --
     * watchPosition success/error are platform event handlers; the rule
     * can't see that. Reads/writes inside them are intentional and safe. */
    watchId = navigator.geolocation.watchPosition(
      (pos) => {
        // An active ghost-walk owns the position feed; drop real fixes so the
        // simulated position is authoritative app-wide (MAP, heat, distance).
        if (simOverride) return;
        // Promote to `tracking` once we see a settled fix; stay there
        // for the remainder of the session even if accuracy later drifts.
        const settled =
          state() === 'tracking' || pos.coords.accuracy <= ACCURACY_SETTLED_THRESHOLD_M;
        if (settled && state() !== 'tracking') setState('tracking');
        const fix: Fix = {
          timestamp: pos.timestamp,
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
          speed: pos.coords.speed,
          heading: pos.coords.heading,
          altitude: pos.coords.altitude,
          simulated: false,
          settled,
        };
        appendFix(fix);
      },
      (err) => {
        setLastError(`${err.code}: ${err.message}`);
        setLastErrorCode(err.code);
        pushEvent('geolocation_error', { code: err.code, message: err.message });
        if (err.code === err.PERMISSION_DENIED) {
          setPermissionState('denied');
        }
        setState('error');
        pushEvent('tracking_stopped', { reason: 'error' });
      },
      WATCH_OPTIONS,
    );
    /* eslint-enable solid/reactivity */
  };

  const stop = () => {
    if (watchId !== null) {
      navigator.geolocation.clearWatch(watchId);
      watchId = null;
    }
    if (state() !== 'idle') pushEvent('tracking_stopped', { reason: 'user' });
    setState('idle');
    setStartedBy(null);
  };

  const pushSimulatedFix = (lat: number, lng: number, speed = 1.4) => {
    if (state() === 'idle') {
      // Synthetic fix flow originates from DIAG's ghost-walk; mark the
      // session as DIAG-owned so MAP doesn't clobber it on unmount.
      setStartedBy('diag');
      setState('acquiring');
      setStartedAt(Date.now());
      pushEvent('tracking_started', { by: 'diag' });
    }
    // Synthetic fixes are perfectly accurate by construction, so they
    // immediately satisfy the settled threshold.
    if (state() !== 'tracking') setState('tracking');
    const fix: Fix = {
      timestamp: Date.now(),
      lat,
      lng,
      accuracy: 5,
      speed,
      heading: null,
      altitude: null,
      simulated: true,
      settled: true,
    };
    appendFix(fix);
  };

  const setSimOverride = (active: boolean) => {
    simOverride = active;
  };

  const clearLog = () => {
    setFixes([]);
    setFixCount(0);
    setLatest(null);
  };

  onCleanup(() => stop());

  return {
    state,
    permission,
    latest,
    fixCount,
    fixes,
    startedAt,
    lastError,
    lastErrorCode,
    startedBy,
    start,
    stop,
    pushSimulatedFix,
    setSimOverride,
    clearLog,
  };
}

/** Shared module-level tracker. Both the DIAG and MAP screens subscribe to
 *  this so a single watchPosition session feeds both views.
 *  Wrapped in createRoot to give the contained signals a stable reactive
 *  owner outside any component scope. The root is never disposed (the
 *  tracker lives for the lifetime of the app). */
export const geolocation: GeolocationTracker = createRoot(() => createGeolocationTracker());
